import React, { use, useEffect, useState } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { FaUsers } from "react-icons/fa";
import { MdPlaylistAdd } from "react-icons/md";
import FantasyTeam from "../Fantasy/Components/FantasyTeam";
import HomepageLeaderBoard from "../HomePage/components/HomepageLeaderBoard";
import {
	CreateLeague,
	initialTeam,
	initialTeamComplete,
	Season,
	Series,
	Sport,
	TeamComplete,
} from "../../types/model";
import { getTeams } from "../../services/teamService";
import { Event } from "../../types/model";
import {
	setSelectedSeason,
	setSelectedDiscipline,
	setTeamLoading,
} from "../../store/slices/fantasySlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import {
	getEventSports,
	getEventSeries,
	filterEventsBySportAndSeries,
	getFutureEvents,
} from "../../utils/helper";
import CreateLeagueModal from "../Fantasy/Components/CreateLeagueModal";
import CreateTeamModal from "../Fantasy/Components/CreateTeamModal";
import SignInToCreateLeagueModal from "../Fantasy/Components/SignInToCreateLeagueModal";
import { getCurrentSeason } from "../../services/seasonService";
import { useNavigate } from "react-router-dom";
import { createLeague } from "../../services/leagueService";

const Teams: React.FC = () => {
	const { isLoggedIn } = useAppSelector((state: RootState) => state.user);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { selectedSeason, selectedSport, selectedSeries, events } =
		useAppSelector((state: RootState) => state.fantasy);

	const [teams, setTeams] = useState<TeamComplete[]>([initialTeamComplete]);
	const [teamLoading, setTeamLoading] = useState<boolean>(false);
	const [showCreateLeague, setShowCreateLeague] = useState<boolean>(false);
	const [showCreateTeam, setShowCreateTeam] = useState<boolean>(false);
	const [showMustSignIn, setShowMustSignIn] = useState<boolean>(false);
	const [currentSeason, setCurrentSeason] = useState<Season | null>(null);

	const filteredEvents = filterEventsBySportAndSeries(
		events,
		selectedSeries ?? undefined,
		selectedSport ?? undefined,
	);

	const futureEvents: Event[] = getFutureEvents(filteredEvents);

	const sortedTeams = [...teams].sort((a, b) => {
		// First sort by whether the team belongs to the current season
		if (a.season === currentSeason?._id && b.season !== currentSeason?._id) {
			return -1; // a comes first
		} else if (
			a.season !== currentSeason?._id &&
			b.season === currentSeason?._id
		) {
			return 1; // b comes first
		}

		return 0;
	});

	useEffect(() => {
		fetchTeams();
		fetchCurrentSeason();
	}, [isLoggedIn]);

	const fetchCurrentSeason = async () => {
		try {
			const res = await getCurrentSeason();
			if (res) {
				setCurrentSeason(res.data);
				dispatch(setSelectedSeason(res.data));
			}
		} catch (error) {
			console.error("Error fetching current season:", error);
		}
	};

	const fetchTeams = async () => {
		try {
			setTeamLoading(true);
			const res = await getTeams({});
			if (res) {
				setTeams(res.data);
			}
		} catch (error) {
			console.error("Error fetching teams:", error);
			setTeams([]);
		} finally {
			setTeamLoading(false);
		}
	};

	const handleCreateLeague = async (leagueData: CreateLeague) => {
		try {
			if (!isLoggedIn) {
				setShowMustSignIn(true);
				return;
			}

			const res = await createLeague(leagueData);
			if (res.status === 200 || res.status === 201) {
				setShowCreateLeague(false);
				const league = res.data;
				navigate(`/league/${league._id}`);
			} else {
				// Handle other status codes
				console.log(
					"Error creating league: Unexpected status code",
					res.status,
				);
			}
		} catch (error: any) {
			console.error("Error creating league:", error);
			if (
				error.response &&
				(error.response.status === 400 || error.response.status === 401)
			) {
				setShowCreateLeague(false);
				alert("Could not create league");

				// setShowMustSignIn(true);
			}
		}
	};

	const handleCreateTeam = async (sport: Sport, series: Series) => {
		try {
			// const res = await getTeams({ sport: sport._id, series: series._id });
			dispatch(setSelectedDiscipline({ sport, series }));
			navigate(`/editteam?sport=${sport._id}&series=${series._id}`);
		} catch (error: any) {
			console.error("Error fetching teams:", error);
			if (
				error.response &&
				(error.response.status === 400 || error.response.status === 401)
			) {
				setShowCreateTeam(false);
				setShowMustSignIn(true);
			}
		}
	};

	return (
		<Container className="teams-page mt-5">
			<Row className="justify-content-md-end">
				<Col xs={12} md={6} className="text-center">
					<Button
						variant="outline-light"
						className=" w-100 fw-bold border-2 mb-md-0 mb-2"
						onClick={() => setShowCreateLeague(true)}
					>
						<FaUsers className="me-2 mb-1" />
						CREATE LEAGUE
					</Button>
				</Col>
				<Col xs={12} md={6} className="text-center">
					<Button
						variant="success"
						className="me-3 w-100 fw-bold mb-3"
						onClick={() => setShowCreateTeam(true)}
					>
						<MdPlaylistAdd className="me-2 mb-1" />
						CREATE TEAM
					</Button>
				</Col>
			</Row>
			{sortedTeams.length > 0 ? (
				<Row>
					{sortedTeams.map((team: TeamComplete) => (
						<Col lg={6} md={12} key={team._id} className="mb-3">
							<FantasyTeam
								team={team}
								isLoading={teamLoading}
								showLeagues={true}
								showLeagueSeries={false}
								showLeagueSport={false}
							/>
						</Col>
					))}
				</Row>
			) : (
				<Row className="justify-content-center mt-5">
					<Col xs={12} md={6} className="text-center">
						<h4 className="teams-page__empty">No teams found</h4>
					</Col>
				</Row>
			)}
			<CreateLeagueModal
				show={showCreateLeague}
				handleClose={() => setShowCreateLeague(false)}
				events={events}
				season={selectedSeason}
				sports={getEventSports(futureEvents)}
				seriesList={getEventSeries(futureEvents)}
				handleCreateLeague={handleCreateLeague}
			/>
			<SignInToCreateLeagueModal
				show={showMustSignIn}
				onHide={() => setShowMustSignIn(false)}
			/>
			<CreateTeamModal
				showCreateTeam={showCreateTeam}
				setShowCreateTeam={setShowCreateTeam}
				handleCreateTeam={handleCreateTeam}
			/>
		</Container>
	);
};

export default Teams;
